import classNames from "classnames/bind";
import { type RootState } from "features/Application/store/store";
import { StrictMode, useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import { OnClickOutside } from "../../../Account/components/OnClickOutside/OnClickOutside";
import { usePageChange } from "../../hooks/usePageChange";
import { AppNavigation, type NavigationLink } from "../AppNavigation/AppNavigation";
import { BreakpointContext } from "../context/BreakpointContext";
import styles from "./AppHeader.module.scss";
import mLogo from "./bsi-logo-sx.svg";
import logo from "./logo-expanded.svg";
import qbLogo from "./quick-base-vector-logo-color.svg";
import smallQbLogo from "./quick-base-vector-logo-sx.svg";
import { useFeatureFlag } from "features/common/hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { userInTeams } from "../../../../tools/teams/teamsService";

const cx = classNames.bind(styles);

export interface Props {
  search: React.ReactNode;
  account: React.ReactNode;
}

export const AppHeader: React.FC<Props> = ({ search, account }) => {
  const { t } = useTranslation("application");
  const enableGroupsInTeams = useFeatureFlag(FeatureFlags.UsingGroupsInEupTeams);
  const [opened, setOpened] = useState(false);
  const { isLargeScreen, isMobileScreen, isTabletScreen } = useContext(BreakpointContext);
  const accountId = useSelector((state: RootState) => state.account.account.current?.id);
  usePageChange(() => setOpened(false));
  const toggle = () => {
    setOpened(s => !s);
  };

  const links = useMemo<NavigationLink[]>(() => {
    let validLinks = [
      {
        to: "/home",
        label: t("mainNavigation.home"),
      },
      {
        to: "/discover",
        label: t("mainNavigation.discover"),
      },
    ];

    if (!userInTeams() || enableGroupsInTeams) {
      validLinks.push({
        to: "/groups",
        label: t("mainNavigation.groups"),
      });
    }

    return validLinks;
  }, [t, enableGroupsInTeams]);

  useEffect(() => {
    setOpened(() => !isMobileScreen);
  }, [isMobileScreen]);

  const classNameMobileMenu = cx(styles.linksMobile, {
    [styles.menuVisible]: isMobileScreen && opened,
  });

  const useAlternateLogo = accountId === window.envtConfig.uniqueLogoAccountId;

  const smallLogo = (
    <img
      src={useAlternateLogo ? smallQbLogo : mLogo}
      className={styles.smallLogo}
      alt={useAlternateLogo ? "quickbase" : "brainstorm small logo"}
    />
  );

  const handleSetOpened = useCallback(() => {
    setOpened(false);
  }, []);

  return (
    <StrictMode>
      <header className={styles.root}>
        <div className={styles.headerWrapper}>
          {isLargeScreen && (
            <Link to={links[0].to} className={styles.logoLink}>
              <img
                src={useAlternateLogo ? qbLogo : logo}
                className={styles.logo}
                alt={useAlternateLogo ? "quickbase" : "brainstorm full logo"}
              />
            </Link>
          )}
          {isTabletScreen && (
            <Link to={links[0].to} className={styles.logoLink}>
              {smallLogo}
            </Link>
          )}
          {isMobileScreen && (
            <OnClickOutside handler={handleSetOpened}>
              <button className={styles.menuToggler} onClick={toggle} aria-label={t("mainNavigation.openAlt")}>
                {smallLogo}
                <Icon name={opened ? "angle up" : "angle down"} />
              </button>
              <div className={classNameMobileMenu}>
                <AppNavigation isMobileView links={links} />
              </div>
            </OnClickOutside>
          )}
          <div className={styles.search}>{search}</div>
          {!isMobileScreen && (
            <div className={styles.links}>{<AppNavigation isMobileView={false} links={links} />}</div>
          )}
          <div className={styles.account}>{account}</div>
        </div>
      </header>
    </StrictMode>
  );
};
